import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { SeverityBadge } from "@/lib/severity";
import { useAuth } from "@/context/AuthContext";
import { TrainFront, X, Pause, Play, ChevronLeft, ChevronRight, RefreshCw } from "lucide-react";
import Gauge from "@/components/dashboard/GaugeChart";
import ParetoChart from "@/components/dashboard/ParetoChart";
import TrendChart from "@/components/dashboard/TrendChart";
import DonutChart from "@/components/dashboard/DonutChart";
import Heatmap from "@/components/dashboard/Heatmap";
import { highlightNumbers } from "@/lib/highlightNumbers";

const CYCLE_MS = 20000; // 20 seconds per panel
const REFRESH_MS = 60000; // refresh data every 60s

function KioskCard({ children, className = "" }) {
  return (
    <div className={`bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-8 ${className}`}>
      {children}
    </div>
  );
}

function KpiTile({ label, value, unit, color = "#93c5fd" }) {
  return (
    <div className="text-center">
      <div className="text-[11px] uppercase tracking-widest text-white/50 font-bold">{label}</div>
      <div className="text-5xl font-black tracking-tight mt-2 text-white tabular-nums">
        {value != null ? (typeof value === "number" ? value.toLocaleString(undefined, { maximumFractionDigits: 2 }) : value) : "—"}
        {unit && <span className="text-2xl ml-1" style={{ color }}>{unit}</span>}
      </div>
    </div>
  );
}

export default function Kiosk() {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const [range, setRange] = useState(null); // {start, end}
  const [summary, setSummary] = useState(null);
  const [pareto, setPareto] = useState({ items: [] });
  const [trend, setTrend] = useState({ items: [], granularity: "daily" });
  const [modes, setModes] = useState({ items: [], total_downtime_min: 0 });
  const [heat, setHeat] = useState({ days: [], bins: [], grid: [] });
  const [recent, setRecent] = useState({ items: [] });
  const [insights, setInsights] = useState([]);
  const [tickAt, setTickAt] = useState(Date.now());
  const [panel, setPanel] = useState(0);
  const [paused, setPaused] = useState(false);

  // Fetch data range on mount
  useEffect(() => {
    (async () => {
      const { data } = await api.get("/metrics/filters");
      if (data.date_max) {
        const maxD = new Date(data.date_max);
        const minD = new Date(maxD); minD.setDate(maxD.getDate() - 29);
        const iso = (d) => d.toISOString().slice(0, 10);
        setRange({ start: iso(minD) < data.date_min ? data.date_min : iso(minD), end: data.date_max });
      } else {
        const end = new Date(); const start = new Date(); start.setDate(end.getDate() - 29);
        setRange({ start: start.toISOString().slice(0, 10), end: end.toISOString().slice(0, 10) });
      }
    })();
  }, []);

  const fetchAll = useCallback(async () => {
    if (!range) return;
    const params = { ...range, line: "all", subsystem: "all" };
    const [s, p, t, m, h, r, i] = await Promise.all([
      api.get("/metrics/summary", { params }),
      api.get("/metrics/pareto", { params }),
      api.get("/metrics/trend", { params }),
      api.get("/metrics/failure_modes", { params }),
      api.get("/metrics/heatmap", { params }),
      api.get("/metrics/recent", { params: { ...params, limit: 8 } }),
      api.get("/metrics/insights", { params }),
    ]);
    setSummary(s.data); setPareto(p.data); setTrend(t.data); setModes(m.data);
    setHeat(h.data); setRecent(r.data); setInsights(i.data.insights || []);
    setTickAt(Date.now());
  }, [range]);

  // Data refresh loop
  useEffect(() => {
    fetchAll();
    const iv = setInterval(fetchAll, REFRESH_MS);
    return () => clearInterval(iv);
  }, [fetchAll]);

  // Panel cycle loop
  const panels = useMemo(() => ["kpi", "pareto", "trend-modes", "heatmap-events"], []);
  useEffect(() => {
    if (paused) return;
    const iv = setInterval(() => setPanel(p => (p + 1) % panels.length), CYCLE_MS);
    return () => clearInterval(iv);
  }, [paused, panels.length]);

  const exit = () => nav("/");

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") exit();
      if (e.key === "ArrowRight") setPanel(p => (p + 1) % panels.length);
      if (e.key === "ArrowLeft") setPanel(p => (p - 1 + panels.length) % panels.length);
      if (e.key === " ") { e.preventDefault(); setPaused(p => !p); }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [panels.length]);

  const c = summary?.current || {};
  const sett = summary?.settings || {};

  return (
    <div className="fixed inset-0 flex flex-col text-white overflow-hidden trams-no-print"
         style={{ background: "linear-gradient(135deg, #0b1220 0%, #0f172a 40%, #111c3a 100%)" }}
         data-testid="kiosk-page">

      {/* Ambient background */}
      <div className="absolute inset-0 opacity-30 pointer-events-none" style={{
        backgroundImage: "radial-gradient(circle at 10% 20%, #1e40af 0%, transparent 40%), radial-gradient(circle at 90% 80%, #3b82f6 0%, transparent 40%)"
      }} />

      {/* Top bar */}
      <div className="relative z-10 flex items-center justify-between px-10 py-6 border-b border-white/10 bg-black/20 backdrop-blur">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-white/10 border border-white/20 flex items-center justify-center">
            <TrainFront className="w-8 h-8" />
          </div>
          <div>
            <div className="text-2xl font-black tracking-tight">TRAMS — RAMS Operations Live</div>
            <div className="text-xs text-white/60 tracking-wide">{range?.start} → {range?.end} · All Lines · All Subsystems</div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <div className="text-3xl font-black tabular-nums leading-none">{new Date(tickAt).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}</div>
            <div className="text-[10px] uppercase tracking-widest text-white/50 mt-1">Live · auto-refresh 60s</div>
          </div>
          <div className="flex items-center gap-1 pl-4 border-l border-white/10">
            <button onClick={() => setPanel(p => (p - 1 + panels.length) % panels.length)} className="p-2 rounded hover:bg-white/10" data-testid="kiosk-prev"><ChevronLeft className="w-5 h-5" /></button>
            <button onClick={() => setPaused(p => !p)} className="p-2 rounded hover:bg-white/10" data-testid="kiosk-pause">{paused ? <Play className="w-5 h-5" /> : <Pause className="w-5 h-5" />}</button>
            <button onClick={() => setPanel(p => (p + 1) % panels.length)} className="p-2 rounded hover:bg-white/10" data-testid="kiosk-next"><ChevronRight className="w-5 h-5" /></button>
            <button onClick={fetchAll} className="p-2 rounded hover:bg-white/10" data-testid="kiosk-refresh"><RefreshCw className="w-5 h-5" /></button>
          </div>
          <button onClick={exit} className="ml-2 flex items-center gap-1.5 px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-sm font-semibold border border-white/20" data-testid="kiosk-exit">
            <X className="w-4 h-4" /> Exit
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 flex-1 overflow-hidden p-10">
        {panel === 0 && (
          <div className="h-full flex flex-col gap-6">
            <div className="grid grid-cols-6 gap-6">
              <KioskCard><KpiTile label="Total Failure Events" value={c.total_failure_events} /></KioskCard>
              <KioskCard><KpiTile label="Total Delay" value={c.total_delay_minutes} unit="min" color="#f59e0b" /></KioskCard>
              <KioskCard><KpiTile label="Cancelled Trips" value={c.cancelled_trips} color="#ef4444" /></KioskCard>
              <KioskCard><KpiTile label="MTBF" value={c.mtbf_hours} unit="h" color="#22c55e" /></KioskCard>
              <KioskCard><KpiTile label="MTTR" value={c.mttr_minutes} unit="min" color="#3b82f6" /></KioskCard>
              <KioskCard><KpiTile label="Availability" value={c.availability_percent} unit="%" color="#22c55e" /></KioskCard>
            </div>
            <KioskCard className="flex-1">
              <div className="text-xs uppercase tracking-widest text-white/50 font-bold mb-6">RAMS Operational Overview</div>
              <div className="grid grid-cols-4 gap-8 h-full">
                <div className="bg-white/95 rounded-xl p-6 flex items-center justify-center"><Gauge label="Reliability (MTBF)" value={c.mtbf_hours} max={Math.max((sett.target_mtbf_hours || 100) * 1.5, c.mtbf_hours || 0)} target={sett.target_mtbf_hours} unit="Hours" direction="up_good" /></div>
                <div className="bg-white/95 rounded-xl p-6 flex items-center justify-center"><Gauge label="Availability (%)" value={c.availability_percent} max={100} target={sett.target_availability} unit="Percent" direction="up_good" /></div>
                <div className="bg-white/95 rounded-xl p-6 flex items-center justify-center"><Gauge label="Maintainability (MTTR)" value={c.mttr_minutes} max={Math.max((sett.target_mttr_minutes || 25) * 2, c.mttr_minutes || 0)} target={sett.target_mttr_minutes} unit="Minutes" direction="down_good" /></div>
                <div className="bg-white/95 rounded-xl p-6 flex items-center justify-center"><Gauge label="Safety (Severity)" value={c.safety_score} max={Math.max((sett.target_safety || 0.10) * 5, c.safety_score || 0)} target={sett.target_safety} unit="per 1M Train Km" direction="down_good" /></div>
              </div>
            </KioskCard>
          </div>
        )}

        {panel === 1 && (
          <KioskCard className="h-full">
            <div className="text-xs uppercase tracking-widest text-white/50 font-bold mb-6">Pareto — Failure by Subsystem</div>
            <div className="bg-white rounded-xl p-6 h-[calc(100%-3rem)]">
              <ParetoChart items={pareto.items} />
            </div>
          </KioskCard>
        )}

        {panel === 2 && (
          <div className="h-full grid grid-cols-2 gap-6">
            <KioskCard>
              <div className="text-xs uppercase tracking-widest text-white/50 font-bold mb-4">Trend — Events & Downtime</div>
              <div className="bg-white rounded-xl p-4 h-[calc(100%-2.5rem)]"><TrendChart items={trend.items} granularity={trend.granularity} /></div>
            </KioskCard>
            <KioskCard>
              <div className="text-xs uppercase tracking-widest text-white/50 font-bold mb-4">Top Failure Modes</div>
              <div className="bg-white rounded-xl p-6 h-[calc(100%-2.5rem)] flex items-center"><DonutChart items={modes.items} total={modes.total_downtime_min} /></div>
            </KioskCard>
          </div>
        )}

        {panel === 3 && (
          <div className="h-full grid grid-cols-5 gap-6">
            <KioskCard className="col-span-3">
              <div className="text-xs uppercase tracking-widest text-white/50 font-bold mb-4">Recent Failure Events</div>
              <div className="bg-white/95 rounded-xl overflow-hidden">
                <table className="w-full text-sm text-slate-900">
                  <thead className="bg-slate-100">
                    <tr>
                      {["Date", "Train", "Subsystem", "Failure Mode", "Impact", "Delay"].map(h => <th key={h} className="text-left px-4 py-3 text-[10px] uppercase tracking-widest font-bold text-slate-600">{h}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {recent.items.map(r => (
                      <tr key={r.id} className="border-t border-slate-100">
                        <td className="px-4 py-2.5">{r.tanggal}</td>
                        <td className="px-4 py-2.5">{r.no_ts ? `T-${r.no_ts}` : "—"}</td>
                        <td className="px-4 py-2.5">{r.equipment || "—"}</td>
                        <td className="px-4 py-2.5 max-w-[220px] truncate" title={r.kejadian}>{r.kejadian || "—"}</td>
                        <td className="px-4 py-2.5 text-xs"><SeverityBadge value={r.dampak} /></td>
                        <td className="px-4 py-2.5 tabular-nums font-semibold">{r.delay_seconds ? Math.round(r.delay_seconds / 60) : 0} min</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </KioskCard>
            <KioskCard className="col-span-2">
              <div className="text-xs uppercase tracking-widest text-white/50 font-bold mb-4">Quick Insights</div>
              <div className="space-y-3">
                {insights.slice(0, 5).map((it) => (
                  <div
                    key={it.id ?? `${it.icon || "i"}-${it.text}`}
                    className="bg-white/5 rounded-lg p-4 border border-white/10 text-sm text-white/90 leading-relaxed"
                  >
                    {highlightNumbers(it.text, "text-[#93c5fd] font-semibold")}
                  </div>
                ))}
              </div>
            </KioskCard>
          </div>
        )}
      </div>

      {/* Panel dots + progress */}
      <div className="relative z-10 px-10 py-4 border-t border-white/10 bg-black/20 backdrop-blur flex items-center justify-between">
        <div className="text-xs text-white/50 uppercase tracking-widest">
          Signed in as <span className="text-white font-semibold">{user?.name}</span> · Press <kbd className="px-1.5 py-0.5 bg-white/10 rounded text-[10px] mx-1">Space</kbd> to pause · <kbd className="px-1.5 py-0.5 bg-white/10 rounded text-[10px]">Esc</kbd> to exit
        </div>
        <div className="flex items-center gap-2" data-testid="kiosk-dots">
          {panels.map((p, i) => (
            <button key={p} onClick={() => setPanel(i)} className={`h-2 rounded-full transition-all ${i === panel ? "w-10 bg-[#3b82f6]" : "w-2 bg-white/20 hover:bg-white/40"}`} />
          ))}
        </div>
      </div>
    </div>
  );
}
